import { PillBadge, GhostButton } from "../Primitives";
import { ROLE_LABELS } from "../../lib/roles";
import { derivedStatus, summarizeCertExpiry } from "./officerUtils";

/**
 * Card-grid view of guards. Same data as the list view, restyled for
 * scannability. Pure presentational.
 */
export function GuardsGrid({ items, totalCount, onEdit, onDelete }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="guards-grid">
      {items.map((g) => {
        const ds = derivedStatus(g);
        const certs = g.certifications || [];
        const { expired, nearing } = summarizeCertExpiry(certs);
        const tone = ds === "deactivated" || ds === "inactive" ? "red"
          : ds === "pending_deactivation" ? "amber"
          : ds === "on_leave" ? "amber"
          : "green";
        return (
          <div
            key={g.id}
            data-testid={`guard-card-${g.id}`}
            className="border border-[#27272A] bg-[#121212] p-4 hover:border-[#FF3B30] transition-colors flex flex-col gap-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-bold uppercase tracking-tight text-base truncate">{g.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 font-mono">
                  #{g.badge_no || "—"} · {ROLE_LABELS[g.role] || ROLE_LABELS.guard}
                </div>
              </div>
              <PillBadge tone={tone}>{ds.replace("_", " ")}</PillBadge>
            </div>

            <div className="text-[11px] font-mono text-zinc-500 truncate">
              {g.email || "—"}{g.phone ? ` · ${g.phone}` : ""}
            </div>
            {g.username && (
              <div
                className="text-[10px] font-mono text-[#34C759]/80 -mt-2"
                data-testid={`guard-username-${g.id}`}
                title="Username used to log in"
              >
                @{g.username}
              </div>
            )}

            <div className="text-xs text-zinc-400">
              <div className="label-mute mb-1">Certifications ({certs.length})</div>
              {certs.length === 0 ? (
                <div className="text-zinc-600 italic text-[11px]">None on file</div>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {certs.slice(0, 4).map((c, idx) => {
                    const label = typeof c === "string" ? c : c.name;
                    const hasDoc = typeof c === "object" && !!c.document_url;
                    const missingDoc = typeof c === "object" && !hasDoc;
                    return (
                      <span
                        key={`${label}-${idx}`}
                        data-testid={missingDoc ? `cert-missing-doc-${g.id}-${idx}` : `cert-${g.id}-${idx}`}
                        title={missingDoc ? "No document uploaded for this certification" : "Document on file"}
                        className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
                          missingDoc
                            ? "bg-[#FF9F0A]/10 border-[#FF9F0A]/40 text-[#FF9F0A]"
                            : "bg-[#1a1a1a] border-[#27272A]"
                        }`}
                      >
                        {label}{missingDoc ? " ⚠" : ""}
                      </span>
                    );
                  })}
                  {certs.length > 4 && <span className="text-[10px] text-zinc-600">+{certs.length - 4}</span>}
                </div>
              )}
            </div>

            {(expired || nearing) > 0 && (
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest border-t border-[#27272A] pt-2">
                {expired > 0 && <span className="text-[#FF3B30]" data-testid={`certs-expired-${g.id}`}>⛔ {expired} expired</span>}
                {nearing > 0 && <span className="text-[#FF9F0A]" data-testid={`certs-nearing-${g.id}`}>⚠ {nearing} ≤30d</span>}
              </div>
            )}

            <div className="flex items-center justify-between mt-auto border-t border-[#27272A] pt-3">
              <div className="text-[11px] font-mono text-zinc-500">${g.hourly_rate || 0}/hr</div>
              <div className="inline-flex gap-1">
                <GhostButton data-testid={`edit-guard-${g.id}`} onClick={() => onEdit(g)}>Edit</GhostButton>
                <GhostButton data-testid={`del-guard-${g.id}`} onClick={() => onDelete(g.id)}>Delete</GhostButton>
              </div>
            </div>
          </div>
        );
      })}
      {items.length === 0 && (
        <div className="border border-[#27272A] bg-[#121212] p-12 text-center text-zinc-500 scanlines md:col-span-2 xl:col-span-3">
          {totalCount === 0 ? "No officers on roster yet." : "No officers match the current filters."}
        </div>
      )}
    </div>
  );
}
